import type { TimeRange } from '@mimir/common/util/timeRange';
import type { VirtualClipModel } from '@mimir/common/dto/virtualClipModel';
import type { ExtendedItemDto } from '@mimir/common/dto/extendedItemDto';
import type { DownloadPlan } from '../fileSync/downloadIdentity';
import { getPremiereSubclipTimeRangeInSeconds } from '../util/premiere';
import { SystemDefaultField } from '@mimir/common/mdf/mdfDefaults';
import { getItemSystemDefaultField } from '@mimir/common/mdf/MetaDataForm';
import type { FuzzyValue } from '@mimir/common/util/fuzzyNumbers';
import type { PremiereSubclip } from '../model/ongoingDownload';
import { getStartTimeSeconds } from '../model/playableItem';
import type { Logger } from '@mimir/common/types/logger';

export type SubclipOriginItem = Pick<ExtendedItemDto, 'id' | 'metadata' | 'timecode' | 'mediaFrameRate' | 'technicalMetadata'>;

function fuzzyToPremiereTimeString(time: FuzzyValue): string {
  return time.approximate.toFixed(2);
}

export function getSubclipToImport(
  logger: Logger,
  plan: DownloadPlan,
  isSubclip: boolean,
  timeRange: TimeRange | null,
  virtualClip: VirtualClipModel | null,
  title: string,
  originItem: SubclipOriginItem
): PremiereSubclip | null {
  const deliver = plan.deliver;
  if (deliver.type == 'DeliverSubclip') {
    // A segment range is handled by the 'DeliverSubclip' delivery type, but
    // if it actually covers the whole media, we don't want to create a
    // subclip object for it, since that would mean a double import
    if ((virtualClip === null) && !isSubclip || (deliver.whole === true)) {
      return null;
    }

    const range = deliver.artifactRange;
    return {
      title,
      startTime: fuzzyToPremiereTimeString(range.start),
      endTime: fuzzyToPremiereTimeString(range.end),
      id: originItem.id,
      premiereStartTimeSeconds: getStartTimeSeconds(logger, originItem, timeRange?.start ? parseFloat(getPremiereSubclipTimeRangeInSeconds(timeRange.start)) : 0),
    };
  }

  if (plan.dlId.artifactReference.type != 'ImplicitReference') {
    return null;
  }

  if (isSubclip) {
    if (!timeRange) {
      return null;
    }
    const offset = virtualClip?.inPoint ?? 0;
    const startTime = getPremiereSubclipTimeRangeInSeconds(timeRange.start, offset);
    return {
      title,
      startTime,
      endTime: getPremiereSubclipTimeRangeInSeconds(timeRange.end, offset),
      id: originItem.id,
      premiereStartTimeSeconds: getStartTimeSeconds(logger, originItem, parseFloat(startTime)),
    };
  }

  if (!virtualClip) {
    return null;
  }

  const startTime = (virtualClip.inPoint / 1000).toFixed(2);
  const endTime = (virtualClip.outPoint / 1000).toFixed(2);
  return {
    startTime,
    endTime,
    title: getItemSystemDefaultField(originItem, SystemDefaultField.Title),
    id: originItem.id,
    premiereStartTimeSeconds: getStartTimeSeconds(logger, originItem, parseFloat(startTime)),
  };
}
